import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { fetchReflectionDetail, fetchPersonDetail, updateActionStatus, fetchConversationActions } from '../../services/dashboardService';
import { ReadOnlyNotice } from '../../components/dashboard/TrialBanner';
import { 
  ArrowLeft, 
  Send, 
  Bot,
  User,
  Loader2,
  Users,
  Info,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Lightbulb,
  Lock,
  Check,
  Square,
  CheckSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Day-specific prompts for the email trial campaign
const DAY_PROMPTS = {
  day2: "After reading today's chapter, what's one situation this week where I could apply these ideas?",
  day3: "Who on my team would benefit most from the approach discussed today?",
  day4: "What's a recent challenge I faced that relates to today's topic?",
  day5: "How has my thinking about leadership changed this week?",
  day6: "Looking back at this week, what patterns do I notice in my leadership moments?",
  day7: "What's the most valuable insight I'm taking from this week's reading?"
};

// Detect if mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const ChatPage = () => {
  const { user, token, isAuthenticated, loading: authLoading, isTrialReadOnly } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  const isMobile = useIsMobile();
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [contextPerson, setContextPerson] = useState(null);
  const [showPromptHint, setShowPromptHint] = useState(false); // Show hint that prompt came from email
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionStates, setActionStates] = useState({}); // Track action completion states

  // Get query params for context
  const continueConversationId = searchParams.get('continue');
  const personAlias = searchParams.get('person');
  const promptId = searchParams.get('prompt'); // Pre-populated prompt from email
  const qrContext = searchParams.get('context'); // QR code context
  const qrSeries = searchParams.get('series');
  const qrBook = searchParams.get('book');
  const qrDay = searchParams.get('day');
  
  // State for QR context banner
  const [qrContextData, setQrContextData] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Load existing conversation or person context
  useEffect(() => {
    const loadContext = async () => {
      setLoading(true);
      try {
        if (continueConversationId) {
          // Continue existing conversation
          const data = await fetchReflectionDetail(continueConversationId);
          setMessages(data.messages || []);
          setConversationId(continueConversationId);
          
          // Load action states for this conversation
          try {
            const actionsData = await fetchConversationActions(continueConversationId);
            const states = {};
            actionsData.actions?.forEach(action => {
              states[action.action_id] = !!action.completed_at;
            });
            setActionStates(states);
          } catch (err) {
            console.error('Error loading action states:', err);
          }
        } else if (personAlias) {
          // Start chat with person context
          const data = await fetchPersonDetail(personAlias);
          setContextPerson(data.person);
        }
        
        // Handle QR code context
        if (qrContext === 'qr' && !continueConversationId) {
          // Try to get full context from sessionStorage
          const storedContext = sessionStorage.getItem('qr_context');
          if (storedContext) {
            const ctx = JSON.parse(storedContext);
            setQrContextData(ctx);
            setInputValue(ctx.reflectionPrompt || '');
            setShowPromptHint(true);
            // Clear from sessionStorage after use
            sessionStorage.removeItem('qr_context');
            // Auto-dismiss hint after 8 seconds
            setTimeout(() => setShowPromptHint(false), 8000);
          } else if (qrSeries && qrBook && qrDay) {
            // Fallback: fetch context from API
            try {
              const response = await fetch(`${API_URL}/api/content/r/${qrSeries}-${qrBook}-day-${qrDay}`);
              if (response.ok) {
                const ctx = await response.json();
                setQrContextData(ctx);
                setInputValue(ctx.reflectionPrompt || '');
                setShowPromptHint(true);
                setTimeout(() => setShowPromptHint(false), 8000);
              }
            } catch (err) {
              console.error('Error loading QR context:', err);
            }
          }
        }
        // Pre-populate input with day prompt if provided (and no conversation to continue)
        else if (promptId && !continueConversationId && DAY_PROMPTS[promptId]) {
          setInputValue(DAY_PROMPTS[promptId]);
          setShowPromptHint(true);
          // Auto-dismiss hint after 5 seconds
          setTimeout(() => setShowPromptHint(false), 5000);
        }
      } catch (err) {
        console.error('Error loading context:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadContext();
    }
  }, [isAuthenticated, continueConversationId, personAlias, promptId, qrContext, qrSeries, qrBook, qrDay]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle action toggle (complete/incomplete)
  const handleActionToggle = async (actionId, completed) => {
    // Set loading state for this action
    setActionStates(prev => ({ ...prev, [`${actionId}_loading`]: true }));
    
    try {
      await updateActionStatus(actionId, completed);
      setActionStates(prev => ({
        ...prev,
        [actionId]: completed,
        [`${actionId}_loading`]: false
      }));
    } catch (err) {
      console.error('Error updating action:', err);
      setActionStates(prev => ({ ...prev, [`${actionId}_loading`]: false }));
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || sending) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setSending(true);
    setError(null);

    // Add user message to UI immediately
    const tempUserMessage = {
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await fetch(`${API_URL}/api/companion/web/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          context_person: contextPerson?.alias,
          device_type: isMobile ? 'mobile' : 'desktop'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const data = await response.json();
      
      // Update conversation ID if new
      if (data.conversation_id && !conversationId) {
        setConversationId(data.conversation_id);
      }

      // Add bot response with structured data
      const botMessage = {
        role: 'assistant',
        content: data.response,
        created_at: new Date().toISOString(),
        actions: data.actions,
        reasoning: data.reasoning,
        reasoning_detail: data.reasoning_detail,
        follow_up: data.follow_up,
        book_reference: data.book_reference
      };
      setMessages(prev => [...prev, botMessage]);
      
      // Initialize action states for new actions (all uncompleted)
      if (data.actions?.length > 0) {
        const newStates = {};
        data.actions.forEach(action => {
          if (action.action_id) {
            newStates[action.action_id] = false;
          }
        });
        setActionStates(prev => ({ ...prev, ...newStates }));
      }

    } catch (err) {
      setError(err.message);
      // Remove the optimistic user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-zinc-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard/reflections" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-500" />
                  Companion
                </h1>
                {contextPerson && (
                  <p className="text-sm text-zinc-500 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Discussing {contextPerson.alias}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            qrContextData ? (
              /* QR Code context — compact, focused view */
              <div className="py-6 px-2" data-testid="qr-context-view">
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5 mb-4">
                  <p className="text-xs text-purple-400/70 uppercase tracking-wider font-medium mb-2">
                    {qrContextData.book?.title || 'First Steps'}
                  </p>
                  <h2 className="text-lg font-semibold text-white mb-3">
                    Day {qrContextData.day?.number}: {qrContextData.day?.title}
                  </h2>
                  {qrContextData.day?.keyTakeaway && (
                    <blockquote className="border-l-2 border-purple-500/40 pl-3 text-sm text-zinc-300 italic leading-relaxed">
                      {qrContextData.day.keyTakeaway}
                    </blockquote>
                  )}
                </div>
                <p className="text-sm text-zinc-500 text-center">
                  Hit send to start reflecting, or edit the prompt below
                </p>
              </div>
            ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-purple-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {contextPerson ? `Chat about ${contextPerson.alias}` : 'Start a Reflection'}
              </h2>
              <p className="text-zinc-400 max-w-md mx-auto mb-6">
                {contextPerson 
                  ? `Share your thoughts about working with ${contextPerson.alias}. I'll help you reflect and develop your approach.`
                  : 'Share a situation, challenge, or thought from your day. I\'ll help you reflect and develop your leadership approach.'}
              </p>
              
              {/* Conversation starters */}
              <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                {getConversationStarters(contextPerson).map((starter, i) => (
                  <button
                    key={i}
                    onClick={() => setInputValue(starter)}
                    className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-full text-sm transition-colors"
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </div>
            )
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => (
                <ChatBubble 
                  key={index} 
                  message={message} 
                  isMobile={isMobile}
                  onActionToggle={handleActionToggle}
                  actionStates={actionStates}
                />
              ))}
              
              {sending && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="bg-zinc-800 rounded-2xl rounded-tl-sm p-4">
                    <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 pb-2">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
            <Info className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-zinc-800 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {isTrialReadOnly ? (
            /* Read-only mode - show upgrade notice instead of input */
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-zinc-400 mb-2">
                <Lock className="w-4 h-4" />
                <span className="text-sm font-medium">Trial Ended — Read-Only Mode</span>
              </div>
              <p className="text-zinc-500 text-sm mb-3">
                You can view your past reflections but cannot create new ones.
              </p>
              <Link
                to="/it-leadership#pricing"
                className="inline-block px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="chat-upgrade-button"
              >
                Upgrade to Continue
              </Link>
            </div>
          ) : (
            /* Normal input */
            <>
              {/* Prompt hint - shows when input was pre-filled from email (not QR - QR shows in main area) */}
              {showPromptHint && !qrContextData && (
                <div 
                  className="mb-2 px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg text-sm text-purple-300"
                  data-testid="prompt-hint"
                >
                  <div className="flex items-center gap-2">
                    {qrContextData ? (
                      <BookOpen className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <Lightbulb className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="flex-1">
                      {qrContextData ? (
                        <>
                          <strong>Day {qrContextData.day?.number}:</strong> {qrContextData.day?.title}
                          <span className="text-purple-400/70"> — {qrContextData.book?.title}</span>
                        </>
                      ) : (
                        "This reflection prompt comes from today's chapter. Feel free to edit or replace it."
                      )}
                    </span>
                    <button 
                      onClick={() => setShowPromptHint(false)}
                      className="ml-auto text-purple-400 hover:text-purple-300"
                    >
                      ×
                    </button>
                  </div>
                  {qrContextData?.day?.keyTakeaway && (
                    <p className="mt-2 text-purple-300/70 italic text-xs pl-6">
                      Key takeaway: "{qrContextData.day.keyTakeaway}"
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <textarea
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    // Dismiss hint when user edits
                    if (showPromptHint) setShowPromptHint(false);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Share your thoughts..."
                  disabled={sending}
                  rows={1}
                  className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700 resize-none disabled:opacity-50"
                  style={{ minHeight: '48px', maxHeight: '200px' }}
                  data-testid="chat-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || sending}
                  className="px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center gap-2"
                  data-testid="chat-send-button"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-zinc-600 mt-2 text-center">
                Web chats are unlimited and don&apos;t count against your weekly allowance
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatBubble = ({ message, isMobile, onActionToggle, actionStates = {} }) => {
  const isUser = message.role === 'user';
  const [showReasoning, setShowReasoning] = useState(!isMobile);
  const [showDetail, setShowDetail] = useState(false);
  const hasStructuredContent = message.actions || message.reasoning;
  
  // For user messages or non-structured responses, render simple bubble
  if (isUser || !hasStructuredContent) {
    return (
      <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-blue-500/10' : 'bg-purple-500/10'
        }`}>
          {isUser ? (
            <User className="w-4 h-4 text-blue-400" />
          ) : (
            <Bot className="w-4 h-4 text-purple-400" />
          )}
        </div>
        <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
          <div className={`inline-block p-4 rounded-2xl ${
            isUser 
              ? 'bg-blue-600 text-white rounded-tr-sm' 
              : 'bg-zinc-800 text-zinc-100 rounded-tl-sm'
          }`}>
            <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-2 prose-li:my-0">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Structured AI response - better layout with separated action cards
  // Desktop: Reasoning first, then Actions
  // Mobile: Actions first (prominent), then collapsible reasoning
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-purple-400" />
      </div>
      <div className="flex-1 max-w-[85%]">
        {/* Mobile: Actions first */}
        {isMobile && message.actions && message.actions.length > 0 && (
          <div className="mb-4" data-testid="chat-actions">
            <p className="text-red-400 font-semibold text-sm mb-3">Your next steps:</p>
            <div className="space-y-2">
              {message.actions.map((action, i) => {
                const actionId = action.action_id;
                const isCompleted = actionId ? actionStates[actionId] : false;
                const isToggling = actionStates[`${actionId}_loading`];
                
                return (
                  <div 
                    key={actionId || i} 
                    className={`border rounded-lg p-3 transition-all ${
                      isCompleted 
                        ? 'bg-green-500/10 border-green-500/20' 
                        : 'bg-red-500/10 border-red-500/20'
                    }`}
                    data-testid={`action-item-${actionId || i}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      {actionId && onActionToggle ? (
                        <button
                          onClick={() => onActionToggle(actionId, !isCompleted)}
                          disabled={isToggling}
                          className={`mt-0.5 flex-shrink-0 transition-colors ${
                            isToggling ? 'opacity-50' : 'hover:opacity-80'
                          }`}
                          data-testid={`action-checkbox-${actionId}`}
                          title={isCompleted ? 'Mark as incomplete' : 'Mark as done'}
                        >
                          {isCompleted ? (
                            <CheckSquare className="w-5 h-5 text-green-400" />
                          ) : (
                            <Square className="w-5 h-5 text-red-400" />
                          )}
                        </button>
                      ) : (
                        <span className="text-red-400 font-bold text-lg leading-none mt-0.5">•</span>
                      )}
                      
                      {/* Action text */}
                      <span className={`prose prose-invert prose-sm max-w-none prose-p:my-0 prose-p:inline flex-1 ${
                        isCompleted ? 'line-through text-zinc-500' : 'text-zinc-100'
                      }`}>
                        <ReactMarkdown>{action.text}</ReactMarkdown>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Reasoning section - collapsible on mobile, shown first on desktop */}
        {message.reasoning && (
          <>
            {isMobile && !showReasoning ? (
              <button 
                onClick={() => setShowReasoning(true)}
                className="text-purple-400 text-sm flex items-center gap-1 hover:text-purple-300 transition-colors mb-2"
                data-testid="show-reasoning-button"
              >
                <ChevronDown className="w-4 h-4" />
                Show reasoning
              </button>
            ) : (
              <div className="bg-zinc-800 rounded-xl p-4 mb-3" data-testid="chat-reasoning">
                {isMobile && (
                  <button 
                    onClick={() => setShowReasoning(false)}
                    className="text-purple-400 text-sm flex items-center gap-1 hover:text-purple-300 transition-colors mb-2"
                  >
                    <ChevronUp className="w-4 h-4" />
                    Hide reasoning
                  </button>
                )}
                <div className="prose prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0 text-zinc-300">
                  <ReactMarkdown>{message.reasoning}</ReactMarkdown>
                </div>
                
                {/* In-depth detail - expandable */}
                {message.reasoning_detail && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    {!showDetail ? (
                      <button
                        onClick={() => setShowDetail(true)}
                        className="text-purple-400 text-sm flex items-center gap-1 hover:text-purple-300 transition-colors"
                        data-testid="show-detail-button"
                      >
                        <ChevronDown className="w-4 h-4" />
                        In-depth analysis
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => setShowDetail(false)}
                          className="text-purple-400 text-sm flex items-center gap-1 hover:text-purple-300 transition-colors mb-2"
                          data-testid="hide-detail-button"
                        >
                          <ChevronUp className="w-4 h-4" />
                          Hide in-depth analysis
                        </button>
                        <div className="prose prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0 text-zinc-400">
                          <ReactMarkdown>{message.reasoning_detail}</ReactMarkdown>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Desktop: Actions after reasoning */}
        {!isMobile && message.actions && message.actions.length > 0 && (
          <div className="mb-4" data-testid="chat-actions">
            <p className="text-red-400 font-semibold text-sm mb-3">Your next steps:</p>
            <div className="space-y-2">
              {message.actions.map((action, i) => {
                const actionId = action.action_id;
                const isCompleted = actionId ? actionStates[actionId] : false;
                const isToggling = actionStates[`${actionId}_loading`];
                
                return (
                  <div 
                    key={actionId || i} 
                    className={`border rounded-lg p-3 transition-all ${
                      isCompleted 
                        ? 'bg-green-500/10 border-green-500/20' 
                        : 'bg-red-500/10 border-red-500/20'
                    }`}
                    data-testid={`action-item-${actionId || i}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      {actionId && onActionToggle ? (
                        <button
                          onClick={() => onActionToggle(actionId, !isCompleted)}
                          disabled={isToggling}
                          className={`mt-0.5 flex-shrink-0 transition-colors ${
                            isToggling ? 'opacity-50' : 'hover:opacity-80'
                          }`}
                          data-testid={`action-checkbox-${actionId}`}
                          title={isCompleted ? 'Mark as incomplete' : 'Mark as done'}
                        >
                          {isCompleted ? (
                            <CheckSquare className="w-5 h-5 text-green-400" />
                          ) : (
                            <Square className="w-5 h-5 text-red-400" />
                          )}
                        </button>
                      ) : (
                        <span className="text-red-400 font-bold text-lg leading-none mt-0.5">•</span>
                      )}
                      
                      {/* Action text */}
                      <span className={`prose prose-invert prose-sm max-w-none prose-p:my-0 prose-p:inline flex-1 ${
                        isCompleted ? 'line-through text-zinc-500' : 'text-zinc-100'
                      }`}>
                        <ReactMarkdown>{action.text}</ReactMarkdown>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Book reference */}
        {message.book_reference && (
          <div className="mt-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4" data-testid="chat-book-reference">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-400 font-semibold text-sm mb-1">
                  {message.book_reference.day_number 
                    ? `Day ${message.book_reference.day_number}: ${message.book_reference.day_title}`
                    : message.book_reference.book_title
                  }
                </p>
                {message.book_reference.book_subtitle && !message.book_reference.day_number && (
                  <p className="text-zinc-400 text-xs mb-2">{message.book_reference.book_subtitle}</p>
                )}
                {message.book_reference.relevance_note && (
                  <p className="text-zinc-300 text-sm mb-2">{message.book_reference.relevance_note}</p>
                )}
                {message.book_reference.key_takeaway && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-zinc-800/50 rounded-lg">
                    <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-zinc-300 text-sm italic">{message.book_reference.key_takeaway}</p>
                  </div>
                )}
                <p className="text-zinc-500 text-xs mt-2">
                  From: {message.book_reference.book_title}
                  {message.book_reference.book_theme && ` • ${message.book_reference.book_theme}`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getConversationStarters = (contextPerson) => {
  if (contextPerson) {
    const rel = contextPerson.relationship;
    if (rel === 'direct_report') {
      return [
        `I need to give ${contextPerson.alias} feedback about...`,
        `${contextPerson.alias} has been struggling with...`,
        `How can I better support ${contextPerson.alias}?`
      ];
    } else if (rel === 'manager') {
      return [
        `I need to discuss something difficult with ${contextPerson.alias}...`,
        `${contextPerson.alias} made a decision I disagree with...`,
        `How do I influence ${contextPerson.alias} on...`
      ];
    } else if (rel === 'peer') {
      return [
        `${contextPerson.alias} and I are having a conflict about...`,
        `I need ${contextPerson.alias}'s buy-in on...`,
        `How can I collaborate better with ${contextPerson.alias}?`
      ];
    }
  }
  
  return [
    "I had a difficult conversation today...",
    "I'm struggling with a decision about...",
    "A team member isn't meeting expectations...",
    "I need to give feedback about..."
  ];
};

export default ChatPage;
